import { useCallback, useRef } from 'react'
import type { ControlProps, GroupBase, InputProps, Props } from 'react-select'
import Select, { ClearIndicatorProps, components, DropdownIndicatorProps } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { useMsg } from '../../../common/localizationUtils'
import { isClient } from '../../../common/nextJsUtils'
import { TOP_NAV_HEIGHT } from '../../../common/scrollUtils'
import { CaretDown, Close } from '../../../icons'
import { useSelectStyle } from '../../../theme/components/ReactSelect'
import { forwardRef, useFormControlContext } from '../../chakra'
import { useFormControlExtendedContext } from './FormControlExtendedContext'

type ReactSelectProps = {
  isCreatable?: boolean
  creatable?: boolean
  id?: string
  menuPortal?: boolean
  isRequired?: boolean
  'data-testid'?: string
}

/**
 * ReactSelect
 *
 * Select component using the React-Select library.
 *
 * Note: This component will eventually replace the VndlySelect component.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-single-select--docs
 */
export const ReactSelect = forwardRef(
  <Option, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    {
      id: _id,
      creatable = false,
      isCreatable = creatable,
      placeholder,
      menuPortal = true,
      'data-testid': dataTestId,
      ...props
    }: Props<Option, IsMulti, Group> & ReactSelectProps,
    ref: any
  ) => {
    const msg = useMsg()
    const formControl = useFormControlContext() ?? {}
    const formControlExt = useFormControlExtendedContext()
    const id = _id ?? formControl.id
    const closeMenuProps = useCloseMenu(id, props)
    const localeNoOptionsMessage = () => {
      return msg('common.ui.no_options')
    }
    const styles = useSelectStyle(props.styles)
    const isReadOnly = formControl.isReadOnly ?? props.isReadOnly
    const isDisabled = formControl.isDisabled ?? props.isDisabled
    const isRequired = formControl.isRequired || props.isRequired || props.required
    const resolvedIsSearchable = isReadOnly ? false : props.isSearchable
    const hasValue = Array.isArray(props.value)
      ? props.value.some(v => !!v)
      : props.value != null && typeof props.value === 'object'
    // react-select renders DummyInput instead of Input when not searchable (e.g. readOnly), and
    // DummyInput only picks up aria-required from react-select's own required prop. Passing it is
    // safe here: the hidden RequiredInput only renders when the select is empty, which hasValue rules out.
    const dummyInputNeedsRequired = isRequired && !resolvedIsSearchable && hasValue
    const isInvalid = formControl.isInvalid
    const selectProps: Props<Option, IsMulti, Group> = {
      placeholder: placeholder === undefined ? msg('common.ui.select_placeholder') : placeholder,
      noOptionsMessage: localeNoOptionsMessage,
      menuPlacement: 'auto',
      ...(menuPortal && isClient() && { menuPortalTarget: document.body }),
      isClearable: !!props.isMulti,
      'aria-errormessage': `${id}-feedback`,
      ...(isInvalid && { 'aria-invalid': isInvalid, isInvalid: true }),
      ...(formControlExt.isWarned && { ...formControlExt.getInputProps?.(), isWarned: true }),
      menuIsOpen: isDisabled || isReadOnly ? false : undefined,
      ...props,
      isDisabled,
      // @ts-ignore
      isReadOnly,
      // @ts-ignore custom prop read by the Input override, react-select only forwards whitelisted aria props
      isRequired,
      required: props.required || dummyInputNeedsRequired || undefined,
      styles,
      components: { Input, DropdownIndicator, ClearIndicator, Control, ...props.components },
      isSearchable: resolvedIsSearchable,
      inputId: id,
      instanceId: `${id}-select`,
      ...closeMenuProps
    }

    return (
      <div data-react-select data-testid={dataTestId}>
        {isCreatable || creatable ? (
          <CreatableSelect ref={ref} {...selectProps} />
        ) : (
          <Select ref={ref} {...selectProps} />
        )}
      </div>
    )
  }
)

ReactSelect.displayName = 'ReactSelect'

const Input = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: InputProps<Option, IsMulti, Group>
) => {
  const { getInputProps } = useFormControlExtendedContext()
  return (
    <components.Input
      data-react-select-input
      {...props}
      {...getInputProps?.(props)}
      aria-required={(props.selectProps as any).isRequired || undefined}
    />
  )
}

const DropdownIndicator = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>
) => (
  <components.DropdownIndicator {...props}>
    <CaretDown />
  </components.DropdownIndicator>
)

const ClearIndicator = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: ClearIndicatorProps<Option, IsMulti, Group>
) => (
  <components.ClearIndicator {...props}>
    <Close />
  </components.ClearIndicator>
)

// Custom Control to allow text selection in read-only mode
// React-select's default onMouseDown calls preventDefault() which blocks text selection
const Control = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  innerProps,
  selectProps,
  ...props
}: ControlProps<Option, IsMulti, Group>) => {
  if (!selectProps.isReadOnly) {
    return <components.Control {...props} innerProps={innerProps} selectProps={selectProps} />
  }
  // If readOnly remove any mouse events that would prevent users from selecting and copying the value text
  const modifiedInnerProps = {
    ...innerProps,
    onMouseDown: undefined,
    onTouchEnd: undefined
  }
  return <components.Control {...props} innerProps={modifiedInnerProps} selectProps={selectProps} />
}

function useCloseMenu<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  id: string,
  props: Props<Option, IsMulti, Group>
) {
  const isMenuOpenRef = useRef(false)
  return {
    closeMenuOnScroll: (event: any) => {
      // If closeMenuOnScroll is true then close the menu when onScroll is triggered.
      // Don't close if scrolling occurs within the select box (horizontal scrolling)
      // or in the menu list (while scrolling through items).
      const select = event.target.closest?.('[data-react-select]')
      const menuList = event.target.closest?.(`[id="react-select-${id}-listbox"]`)
      if (props.closeMenuOnScroll && !select && !menuList) return true

      // If the menu isn't open, don't perform any calculations
      if (!isMenuOpenRef.current) return false

      // Grab the menu list element and calculate it's top position
      if (!menuList) return false

      // If top of menu list is above the bottom of the header, then close the menu
      const top = menuList.getBoundingClientRect().top
      if (top < TOP_NAV_HEIGHT) return true
      return false
    },
    // These two functions keep track of when the menu is open or not
    /*
        eslint-disable-next-line react-hooks/preserve-manual-memoization --

        ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
        Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
        There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
        couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

        Issue: `props.onMenuOpen` and `props.onMenuClose` are used inside the callbacks but deps are empty.
        Possible fix approach:
          // Option 1: Use refs to hold the prop callbacks:
          const onMenuOpenRef = useRef(props.onMenuOpen)
          onMenuOpenRef.current = props.onMenuOpen
          onMenuOpen: useCallback((...args: []) => {
            isMenuOpenRef.current = true
            return onMenuOpenRef.current?.(...args)
          }, [])

          // Option 2: Remove useCallback entirely - the memoization is not needed (verify performance is acceptable):
          onMenuOpen: (...args: []) => {
            isMenuOpenRef.current = true
            return props.onMenuOpen?.(...args)
          }
    */
    onMenuOpen: useCallback((...args: []) => {
      isMenuOpenRef.current = true
      return props.onMenuOpen?.(...args)
    }, []),
    onMenuClose: useCallback(
      /*
          eslint-disable-next-line react-hooks/preserve-manual-memoization --

          ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
          Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
          There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
          couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

          Issue: `props.onMenuClose` is used inside the callback but deps are empty.
          Possible fix approach:
            // Option 1: Use a ref to hold the prop callback:
            const onMenuCloseRef = useRef(props.onMenuClose)
            onMenuCloseRef.current = props.onMenuClose
            onMenuClose: useCallback((...args: []) => {
              isMenuOpenRef.current = false
              return onMenuCloseRef.current?.(...args)
            }, [])

            // Option 2: Remove useCallback entirely - the memoization is not needed (verify performance is acceptable):
            onMenuClose: (...args: []) => {
              isMenuOpenRef.current = false
              return props.onMenuClose?.(...args)
            }
      */
      (...args: []) => {
        isMenuOpenRef.current = false
        return props.onMenuClose?.(...args)
      },
      []
    )
  }
}
